import React, { useState, useEffect } from 'react';
import { useTheme } from '../../../hooks/useTheme';
import { useLoader } from '../../../hooks/useLoader';
import { TestSummary } from '../TestExecution/const/TestSummary';
import testExecutionService, { TestExecutionSummaryResponse } from '../../../api/testExecutionService';
import { useSwobNotifications } from '../../../hooks/NotificationsProvider';
import { AxiosError } from 'axios';
import styles from './TestExecutionSummary.module.css';
import ReactEDSWrapper from '../../../utils/react-eds-wrapper';
import { Loading } from '@dsi/react-eds';
import TestExecutionSummaryMetadata from './TestExecutionSummaryMetadata';
import TestSummaryStatistics from './TestSummaryStatistics';
import TestCaseTable from './TestCaseTable';

interface TestExecutionSummaryProps {
  testExecutionId: string | null;
  isDialog?: boolean;
}

const TestExecutionSummary: React.FC<TestExecutionSummaryProps> = ({ testExecutionId, isDialog }) => {
  const { theme } = useTheme();
  const { showLoader, hideLoader } = useLoader();
  const { addNotifications } = useSwobNotifications();
  const [testSummary, setTestSummary] = useState<TestSummary | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    const fetchTestSummary = async () => {
      if (!testExecutionId) {
        setTestSummary(null);
        return;
      }

      setLoading(true);
      if (!isDialog) {
        showLoader();
      }

      try {
        const response: TestExecutionSummaryResponse = await testExecutionService.getTestExecutionSummary(testExecutionId);

        // Map API response to TestSummary type
        const summary: TestSummary = {
          test_execution_id: response.test_execution_id,
          test_collection_name: response.test_collection_name,
          workflow_id: response.workflow_id,
          product_version: response.product_version,
          product_number: response.product_number,
          start_timestamp: response.start_timestamp,
          duration: response.duration,
          statistics: response.statistics
        };

        setTestSummary(summary);
      } catch (error: unknown) {
        // Show error notification
        addNotifications([
          {
            title: 'Error',
            icon: 'icon-triangle-warning',
            description:
              error && error instanceof AxiosError
                ? error.response?.data?.message || error.message
                : 'An error occurred while fetching test execution summary',
            timestamp: new Date(),
            color: 'red'
          }
        ]);
        setTestSummary(null);
      } finally {
        setLoading(false);
        hideLoader();
      }
    };

    fetchTestSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [testExecutionId, addNotifications]);

  if (loading) {
    return (
      <div
        className={`${styles.container} ${styles.containerPadding} ${styles.secondaryTile} ${theme ? styles.containerLight : styles.containerDark}`}
      >
        <div className={styles.title}>Test Execution Summary</div>

        <div className={styles.loadingContainer}>
          <ReactEDSWrapper component={Loading} size="large" />
        </div>
      </div>
    );
  }

  if (!testSummary) {
    return (
      <div
        className={`${styles.container} ${styles.containerPadding} ${styles.secondaryTile} ${theme ? styles.containerLight : styles.containerDark}`}
      >
        <div className={styles.title}>Test Execution Summary</div>
        <div>No data available</div>
      </div>
    );
  }

  function getClasses(): string {
    if (!isDialog) {
      return `${styles.container} ${styles.containerPadding} ${styles.secondaryTile} ${theme ? styles.containerLight : styles.containerDark}`;
    }
    return '';
  }

  return (
    <div className={getClasses()}>
      {!isDialog && (
        <>
          <div className={styles.title}>Test Execution Summary</div>
          <div className={styles.subTitle}>ID-{testSummary.test_execution_id}</div>
        </>
      )}

      <TestExecutionSummaryMetadata testSummary={testSummary} testExecutionId={testSummary.test_execution_id} />
      <TestSummaryStatistics statistics={testSummary.statistics} isDialog={isDialog} />
      <TestCaseTable testExecutionId={testSummary.test_execution_id} />
    </div>
  );
};

export default TestExecutionSummary;
